import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Button, Form } from 'react-bootstrap';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../../authConfig';
import { ConfigurationDataService } from '../../Services/ConfigurationParameterDataService';
import StorageProgressBar from '../../Components/StorageProgressBar/StorageProgressBar';

const AdvancedConfiguration = () => {
    const { instance, accounts, inProgress } = useMsal();
    const [configurations, setConfigurations] = useState([]);
    const [changedConfigs, setChangedConfigs] = useState({}); // To track changed configuration values
    const [storage, setStorage] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (inProgress === "none" && accounts.length > 0) {
            fetchConfigurations();
            fetchStorageInfo();
        }
    }, [accounts, inProgress]);

    const fetchConfigurations = async () => {
        try {
            const request = {
                ...loginRequest,
                account: accounts[0]
            };
            const response = await instance.acquireTokenSilent(request);
            const configs = await ConfigurationDataService.getAllConfigurations(response.accessToken);
            setConfigurations(configs);
            setLoading(false);
        } catch (error) {
            console.error('Failed to fetch configurations:', error);
        }
    };

    const fetchStorageInfo = async () => {
        try {
            const request = {
                ...loginRequest,
                account: accounts[0]
            };
            const response = await instance.acquireTokenSilent(request);

            // Fetch used storage
            const usedStorage = await ConfigurationDataService.getTotalAvailableStorage(response.accessToken);

            // Fetch total storage capacity
            const storageCapacity = await ConfigurationDataService.getConfigurationByName('BlobStorageTotalCapacity', response.accessToken);

            setStorage({
                usedStorage: usedStorage,
                storageLimit: parseInt(storageCapacity.value, 10) * 1073741824
            })
        } catch (error) {
            console.error('Failed to fetch storage info:', error);
        }
    };

    // Handle change in configuration values
    const handleChange = (e, configId, dataType) => {
        const { name, value, checked, type } = e.target;
        const updatedValue = dataType === 'boolean' ? checked : value;

        setChangedConfigs(prevState => ({
            ...prevState,
            [configId]: { ...prevState[configId], id: configId, name, value: updatedValue }
        }));
    };

    // Save only the changed configurations
    const handleSave = async () => {
        try {
            const request = {
                ...loginRequest,
                account: accounts[0]
            };
            const response = await instance.acquireTokenSilent(request);
    
            // Prepare the changed configurations for update
            const changedParams = Object.values(changedConfigs).map(config => ({
                id: config.id,
                value: config.value.toString()
            }));
    
            if (changedParams.length > 0) {
                await ConfigurationDataService.updateConfigurationValues(changedParams, response.accessToken);
                alert('Configuration values updated successfully.');
                fetchConfigurations(); // Refresh the configurations
                fetchStorageInfo();
                setChangedConfigs({}); // Reset changed configs tracker
            } else {
                alert('No changes made.');
            }
        } catch (error) {
            console.error('Failed to update configuration values:', error);
        }
    };
    

    // Group configurations by category
    const groupByCategory = (configs) => {
        return configs.reduce((groupedConfigs, config) => {
            const category = config.category || 'General';
            if (!groupedConfigs[category]) {
                groupedConfigs[category] = [];
            }
            groupedConfigs[category].push(config);
            return groupedConfigs;
        }, {});
    };

    const groupedConfigs = groupByCategory(configurations);

    return (
        <Container fluid className='h-100'>
            <Row className='h-100'>
                <Col className='d-flex flex-column h-100'>
                    <h2>Advanced Configuration</h2>
                    {storage ? (
                        <div className='my-3'>
                            <h6>Total storage available</h6>
                            <StorageProgressBar storage={storage} />
                        </div>
                    ) : (
                        <span>Loading storage information...</span>
                    )}
                    {loading ? (
                        <p>Loading configurations...</p>
                    ) : (
                        <Form className='overflow-y-auto flex-grow-1'>
                            {Object.keys(groupedConfigs).map((category, index) => (
                                <div key={index} className="mb-4">
                                    <h4>{category}</h4>
                                    {groupedConfigs[category]
                                        .sort((a, b) => a.order - b.order) // Sort by Order within the category
                                        .map(config => (
                                            <Form.Group className="mb-2" key={config.id}>
                                                <Form.Label className='fw-bold'>{config.name}</Form.Label>
                                                {config.dataType === 'boolean' ? (
                                                    <Form.Check
                                                        type="switch"
                                                        name={config.name}
                                                        label={config.description}
                                                        checked={changedConfigs[config.id]?.value ?? config.value === 'true'}
                                                        onChange={(e) => handleChange(e, config.id, config.dataType)}
                                                    />
                                                ) : (
                                                    <>
                                                        <Form.Control
                                                            type={config.dataType === 'int' ? 'number' : 'text'}
                                                            name={config.name}
                                                            value={changedConfigs[config.id]?.value ?? config.value} // Use nullish coalescing to allow empty strings
                                                            onChange={(e) => handleChange(e, config.id, config.dataType)}
                                                        />
                                                        <small>{config.description}</small>
                                                    </>
                                                )}
                                            </Form.Group>
                                        ))}
                                </div>
                            ))}
                        </Form>
                    )}
                    <div className='d-flex justify-content-end mt-4'>
                    <Button className="px-4" onClick={handleSave}>
                        Save Changes
                    </Button>
                    </div>
                </Col>
            </Row>
        </Container>
    );
};

export default AdvancedConfiguration;
